import { KUFER_URL } from '@xver/constants/env';
import { useCallback, useEffect, useRef, useState } from 'react';

export type LogStreamStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

interface KuferLogMessage {
    type: 'connected' | 'log' | 'error' | string;
    data?: string;
    message?: string;
}

interface UseLogStreamOptions {
    enabled: boolean;
    requestTicket: () => Promise<{ ticket: string; expires_in: number }>;
    buildWsUrl: (ticket: string) => string;
    maxLines?: number;
    onServerClose?: () => void;
}

interface UseLogStreamReturn {
    logs: string[];
    status: LogStreamStatus;
    error: string | null;
    connect: () => void;
    disconnect: () => void;
    clear: () => void;
}

function toWebSocketBaseUrl(httpUrl: string): string {
    if (httpUrl.startsWith('https://')) {
        return 'wss://' + httpUrl.slice('https://'.length);
    }
    if (httpUrl.startsWith('http://')) {
        return 'ws://' + httpUrl.slice('http://'.length);
    }
    return httpUrl;
}

export function buildKuferContainerLogsUrl(containerId: string, ticket: string): string {
    const base = toWebSocketBaseUrl(KUFER_URL);
    return `${base}/v1/containers/${containerId}/logs/stream?ticket=${encodeURIComponent(ticket)}`;
}

export function buildKuferBuildLogsUrl(containerId: string, ticket: string): string {
    const base = toWebSocketBaseUrl(KUFER_URL);
    return `${base}/v1/containers/${containerId}/builds/logs/stream?ticket=${encodeURIComponent(ticket)}`;
}

export default function useLogStream({
    enabled,
    requestTicket,
    buildWsUrl,
    maxLines = 2000,
    onServerClose,
}: UseLogStreamOptions): UseLogStreamReturn {
    const [logs, setLogs] = useState<string[]>([]);
    const [status, setStatus] = useState<LogStreamStatus>('idle');
    const [error, setError] = useState<string | null>(null);

    const activeControllerRef = useRef<AbortController | null>(null);
    const onServerCloseRef = useRef<(() => void) | undefined>(onServerClose);
    useEffect(() => {
        onServerCloseRef.current = onServerClose;
    }, [onServerClose]);

    const openStream = useCallback(
        async (signal: AbortSignal) => {
            setStatus('connecting');
            setError(null);

            let ticket: string;
            try {
                const result = await requestTicket();
                ticket = result.ticket;
            } catch (err) {
                if (signal.aborted) return;
                const message =
                    err && typeof err === 'object' && 'message' in err
                        ? String((err as { message: unknown }).message)
                        : 'failed to obtain log streaming ticket';
                setStatus('error');
                setError(message);
                return;
            }

            if (signal.aborted) return;

            const url = buildWsUrl(ticket);
            const socket = new WebSocket(url);

            const abortHandler = () => {
                if (socket.readyState !== WebSocket.CLOSED) socket.close();
            };
            signal.addEventListener('abort', abortHandler);

            socket.onopen = () => {
                if (signal.aborted) return;
                setStatus('open');
            };

            socket.onmessage = (event) => {
                if (signal.aborted) return;
                let parsed: KuferLogMessage;
                try {
                    parsed = JSON.parse(event.data);
                } catch {
                    setLogs((prev) => trimLogs([...prev, event.data], maxLines));
                    return;
                }

                if (parsed.type === 'log' && parsed.data) {
                    setLogs((prev) => trimLogs([...prev, parsed.data ?? ''], maxLines));
                } else if (parsed.type === 'error') {
                    setError(parsed.message ?? 'unknown error');
                    setStatus('error');
                }
            };

            socket.onerror = () => {
                if (signal.aborted) return;
                setStatus('error');
                setError((prev) => prev ?? 'websocket error');
            };

            socket.onclose = () => {
                signal.removeEventListener('abort', abortHandler);
                if (signal.aborted) return;
                setStatus((prev) => (prev === 'error' ? prev : 'closed'));
                onServerCloseRef.current?.();
            };
        },
        [requestTicket, buildWsUrl, maxLines]
    );

    const disconnect = useCallback(() => {
        const controller = activeControllerRef.current;
        if (controller && !controller.signal.aborted) {
            controller.abort();
        }
        activeControllerRef.current = null;
        setStatus((prev) => (prev === 'error' ? prev : 'closed'));
    }, []);

    const connect = useCallback(() => {
        if (activeControllerRef.current && !activeControllerRef.current.signal.aborted) {
            activeControllerRef.current.abort();
        }
        const controller = new AbortController();
        activeControllerRef.current = controller;
        void openStream(controller.signal);
    }, [openStream]);

    const clear = useCallback(() => {
        setLogs([]);
    }, []);

    useEffect(() => {
        if (!enabled) return;
        const controller = new AbortController();
        activeControllerRef.current = controller;
        void openStream(controller.signal);
        return () => {
            controller.abort();
            if (activeControllerRef.current === controller) {
                activeControllerRef.current = null;
            }
        };
    }, [enabled, openStream]);

    return { logs, status, error, connect, disconnect, clear };
}

function trimLogs(lines: string[], maxLines: number): string[] {
    if (lines.length <= maxLines) return lines;
    return lines.slice(lines.length - maxLines);
}
